'use client';

import { MoreHorizontal } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@data-delivery/d3s';
import { type MenuItem } from './nav-main';

export type BreadcrumbConfig = {
  path: string;
  label: string;
  isClickable?: boolean;
};

export interface DynamicBreadcrumbOptions {
  baseCrumbs?: () => BreadcrumbConfig[];
  parentResolver?: (
    currentItem: MenuItem,
    allItems: MenuItem[],
    pathname: string,
  ) => BreadcrumbConfig | undefined;
  segmentLabelFormatter?: (segment: string, index: number, segments: string[]) => string;
  fallbackClickablePredicate?: (segmentPath: string, isLast: boolean) => boolean;
  collapseStrategy?: (crumbs: BreadcrumbConfig[]) => BreadcrumbConfig[];
  /**
   * Optional function to provide router state for a breadcrumb link.
   * Return an object to pass as `state` to React Router's Link, or undefined for no state.
   */
  getLinkState?: (crumb: BreadcrumbConfig) => Record<string, unknown> | undefined;
}

const findMenuItemByUrl = (items: MenuItem[], url: string): MenuItem | undefined => {
  for (const item of items) {
    if (item.url === url) {
      return item;
    }

    if (item.items) {
      for (const subItem of item.items) {
        if (subItem.url === url) {
          return { ...subItem, icon: item.icon };
        }
      }
    }
  }

  return undefined;
};

const defaultOptions: Required<DynamicBreadcrumbOptions> = {
  baseCrumbs() {
    return [{ path: '/', label: 'Home', isClickable: true }];
  },
  getLinkState() {
    return undefined;
  },
  parentResolver(currentItem, allItems) {
    let parent: MenuItem | undefined;
    for (const item of allItems) {
      const hasChild = item.items?.some(sub => sub.url === currentItem.url);
      if (hasChild) {
        parent = item;
        break;
      }
    }

    if (!parent) {
      return undefined;
    }

    let resolvedParentPath = parent.url;
    let clickable = parent.url !== '#';
    if (parent.url === '#') {
      const childSegments = currentItem.url.split('/').filter(Boolean);
      if (childSegments.length > 0) {
        resolvedParentPath = '/' + childSegments[0];
        const hasExplicit = allItems.some(mi => mi.url === resolvedParentPath);
        clickable = hasExplicit || resolvedParentPath === '/configuration';
      } else {
        resolvedParentPath = '/';
        clickable = true;
      }
    }

    return { path: resolvedParentPath, label: parent.title, isClickable: clickable };
  },
  segmentLabelFormatter(segment: string) {
    return segment
      .split('-')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  },
  fallbackClickablePredicate(_segmentPath, isLast) {
    return !isLast;
  },
  collapseStrategy(crumbs) {
    return crumbs;
  },
};

const generateBreadcrumbsFromNavigation = (
  pathname: string,
  navigationItems: MenuItem[],
  options?: DynamicBreadcrumbOptions,
): BreadcrumbConfig[] => {
  const opts = { ...defaultOptions, ...(options || {}) };
  const breadcrumbs: BreadcrumbConfig[] = [...opts.baseCrumbs()];

  const currentItem = findMenuItemByUrl(navigationItems, pathname);
  if (currentItem) {
    const parentCrumb = opts.parentResolver(currentItem, navigationItems, pathname);
    if (parentCrumb) {
      breadcrumbs.push(parentCrumb);
    }

    breadcrumbs.push({ path: currentItem.url, label: currentItem.title, isClickable: false });
  } else {
    const segments = pathname.split('/').filter(Boolean);
    let currentPath = '';
    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const isLast = index === segments.length - 1;

      const decodedSegment = (() => {
        try {
          return decodeURIComponent(segment);
        } catch {
          return segment;
        }
      })();
      const label = opts.segmentLabelFormatter(decodedSegment, index, segments);
      breadcrumbs.push({
        path: currentPath,
        label,
        isClickable: opts.fallbackClickablePredicate(currentPath, isLast),
      });
    });
  }

  return opts.collapseStrategy(breadcrumbs);
};

export interface DynamicBreadcrumbProps {
  className?: string;
  navigationItems: MenuItem[];
  options?: DynamicBreadcrumbOptions;
}

export function DynamicBreadcrumb({ className, navigationItems, options }: DynamicBreadcrumbProps) {
  const location = useLocation();
  const opts = { ...defaultOptions, ...(options || {}) };

  const breadcrumbs = generateBreadcrumbsFromNavigation(
    location.pathname,
    navigationItems,
    options,
  );

  if (breadcrumbs.length <= 1) {
    return null;
  }

  const renderBreadcrumbs = () => {
    if (breadcrumbs.length >= 4) {
      const first = breadcrumbs[0];
      const middle = breadcrumbs.slice(1, -2);
      const secondToLast = breadcrumbs[breadcrumbs.length - 2];
      const last = breadcrumbs[breadcrumbs.length - 1];

      return (
        <>
          {/* Home */}
          <div className='flex items-center'>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={first.path} state={opts.getLinkState(first)}>{first.label}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </div>

          {/* Dropdown for middle items */}
          <div className='flex items-center'>
            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger className='flex h-9 w-9 items-center justify-center rounded-md border-0 bg-transparent px-0 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50'>
                  <MoreHorizontal className='h-4 w-4' />
                  <span className='sr-only'>Toggle menu</span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='start'>
                  {middle.map(crumb => (
                    <DropdownMenuItem key={crumb.path} asChild>
                      {crumb.isClickable ? (
                        <Link to={crumb.path} state={opts.getLinkState(crumb)} className='cursor-pointer'>
                          {crumb.label}
                        </Link>
                      ) : (
                        <span>{crumb.label}</span>
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </div>

          {/* Parent (if different from home) */}
          {secondToLast.path !== first.path && (
            <div className='flex items-center'>
              <BreadcrumbItem>
                {secondToLast.isClickable ? (
                  <BreadcrumbLink asChild>
                    <Link to={secondToLast.path} state={opts.getLinkState(secondToLast)}>{secondToLast.label}</Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{secondToLast.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
              <BreadcrumbSeparator />
            </div>
          )}

          {/* Current page */}
          <div className='flex items-center'>
            <BreadcrumbItem>
              <BreadcrumbPage className='truncate'>{last.label}</BreadcrumbPage>
            </BreadcrumbItem>
          </div>
        </>
      );
    }

    return breadcrumbs.map((crumb, index) => (
      <div key={crumb.path} className='flex items-center'>
        <BreadcrumbItem>
          {crumb.isClickable ? (
            <BreadcrumbLink asChild>
              <Link to={crumb.path} state={opts.getLinkState(crumb)} className='truncate max-w-[200px]'>
                {crumb.label}
              </Link>
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage className='truncate max-w-[200px]'>{crumb.label}</BreadcrumbPage>
          )}
        </BreadcrumbItem>
        {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
      </div>
    ));
  };

  return (
    <Breadcrumb className={className}>
      <BreadcrumbList className='max-w-[600px] overflow-hidden'>
        {renderBreadcrumbs()}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
